import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@linaria/react';
import { COLORS } from '@livecommerce/design-system';
import useClickOutside from '../../hooks/useClickOutside.js';

const Wrapper = styled.div`
  border-radius: 6px;
  position: relative;

  &:hover {
    background-color: ${(props) => props.hoverBackgroundColor};

    & > .dropdown {
      display: ${(props) => props.display};
    }
  }
`;

const DropdownMenu = styled.div`
  display: ${(props) => (props.isOpened ? 'flex' : 'none')};
  position: absolute;
  top: ${(props) => props.top};
  bottom: ${(props) => props.bottom};
  left: ${(props) => props.left};
  right: ${(props) => props.right};
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${(props) => props.dropdownBorderRadius};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.04), 0 16px 24px rgba(0, 0, 0, 0.06);
  background-color: white;
  z-index: 9;

  &::before {
    content: '';
    position: absolute;
    left: ${(props) => props.before.left};
    right: ${(props) => props.before.right};
    bottom: ${(props) => props.before.bottom};
    top: ${(props) => props.before.top};
    width: ${(props) => props.before.width};
    height: ${(props) => props.before.height};
    background-color: transparent;
  }
`;

const getMenuHoverDisplay = (isControlled, isOpened) => {
  if (!isControlled) return 'flex';
  if (isOpened) return 'flex';

  return 'none';
};

const getTopPosition = ({ dropdownAlignment, dropdownPosition, dropdownVerticalOffset, dropdownGap }) => {
  if (dropdownPosition === 'top') return 'initial';
  if (dropdownPosition === 'bottom') return `calc(100% + ${dropdownGap}px)`;

  if (dropdownAlignment === 'start') return `${0 + dropdownVerticalOffset}px`;
  if (dropdownAlignment === 'end') return 'initial';

  return 'initial';
};

const getRightPosition = ({ dropdownAlignment, dropdownPosition, dropdownHorizontalOffset, dropdownGap }) => {
  if (dropdownPosition === 'right') return 'initial';
  if (dropdownPosition === 'left') return `calc(100% + ${dropdownGap}px)`;

  if (dropdownAlignment === 'start') return 'initial';
  if (dropdownAlignment === 'end') return `${0 + dropdownHorizontalOffset}px`;

  return 'initial';
};

const getBottomPosition = ({ dropdownAlignment, dropdownPosition, dropdownVerticalOffset, dropdownGap }) => {
  if (dropdownPosition === 'bottom') return 'initial';
  if (dropdownPosition === 'top') return `calc(100% + ${dropdownGap}px)`;

  if (dropdownAlignment === 'start') return 'initial';
  if (dropdownAlignment === 'end') return `${0 + dropdownVerticalOffset}px`;

  return 'initial';
};

const getLeftPosition = ({ dropdownAlignment, dropdownPosition, dropdownHorizontalOffset, dropdownGap }) => {
  if (dropdownPosition === 'right') return `calc(100% + ${dropdownGap}px)`;
  if (dropdownPosition === 'left') return 'initial';

  if (dropdownAlignment === 'start') return `${0 + dropdownHorizontalOffset}px`;
  if (dropdownAlignment === 'end') return 'initial';

  return 'initial';
};

const getBeforeStyles = ({ dropdownPosition, dropdownGap }) => {
  const vertical = dropdownPosition === 'top' || dropdownPosition === 'bottom';
  const horizontal = dropdownPosition === 'left' || dropdownPosition === 'right';

  const beforeStyles = {
    left: 'initial',
    right: 'initial',
    bottom: 'initial',
    top: 'initial',
    width: vertical ? '100%' : `${dropdownGap}px`,
    height: horizontal ? '100%' : `${dropdownGap}px`,
  };

  return {
    ...beforeStyles,
    [dropdownPosition]: '100%',
  };
};

const NavigationDropdown = ({
  dropdownContent,
  children,
  isControlled = false,
  isOpened = false,
  onClickOutside = () => undefined,
  dropdownGap = 8,
  dropdownHorizontalOffset = 0,
  dropdownVerticalOffset = 0,
  dropdownPosition = 'right',
  dropdownAlignment = 'end',
  dropdownBorderRadius = '6px',
  hoverBackgroundColor = COLORS.white,
  ...props
}) => {
  const { ref } = useClickOutside(() => {
    if (isOpened) onClickOutside();
  });

  const top = getTopPosition({ dropdownAlignment, dropdownPosition, dropdownVerticalOffset, dropdownGap });
  const right = getRightPosition({ dropdownAlignment, dropdownPosition, dropdownHorizontalOffset, dropdownGap });
  const bottom = getBottomPosition({ dropdownAlignment, dropdownPosition, dropdownVerticalOffset, dropdownGap });
  const left = getLeftPosition({ dropdownAlignment, dropdownPosition, dropdownHorizontalOffset, dropdownGap });

  return (
    <Wrapper
      {...props}
      ref={ref}
      hoverBackgroundColor={hoverBackgroundColor}
      display={getMenuHoverDisplay(isControlled, isOpened)}
      onClick={(e) => e.stopPropagation()}
    >
      <DropdownMenu
        className="dropdown"
        aria-hidden={!isOpened}
        isOpened={isOpened}
        top={top}
        right={right}
        bottom={bottom}
        left={left}
        before={getBeforeStyles({ dropdownPosition, dropdownGap })}
        dropdownBorderRadius={dropdownBorderRadius}
      >
        {dropdownContent}
      </DropdownMenu>
      {children}
    </Wrapper>
  );
};

NavigationDropdown.propTypes = {
  dropdownContent: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  menuClassname: PropTypes.object,
  isControlled: PropTypes.bool,
  isOpened: PropTypes.bool,
  onClickOutside: PropTypes.func,
  dropdownGap: PropTypes.number,
  dropdownHorizontalOffset: PropTypes.number,
  dropdownVerticalOffset: PropTypes.number,
  dropdownPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  dropdownAlignment: PropTypes.oneOf(['start', 'center', 'end']),
  hoverBackgroundColor: PropTypes.string,
};

export default NavigationDropdown;
